import React from 'react';
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';
import type { NonAdmitReason } from '../../services/api/types.gen';
import {
  NON_ADMIT_REASON_DETAILS_MAX_LENGTH,
  NON_ADMIT_REASON_OPTIONS,
  NON_ADMIT_REASON_REQUIRING_DETAILS,
} from '../../constants/nonAdmitReasons';

/** Sentinel for the "no reason" item. Radix Select forbids an empty value. */
const SKIP_VALUE = '__skip__';

export interface NonAdmitReasonFieldsProps {
  reason: NonAdmitReason | null;
  details: string;
  onReasonChange: (reason: NonAdmitReason | null) => void;
  onDetailsChange: (details: string) => void;
  /** Distinguishes the field ids when two of these ever share a page. */
  idPrefix?: string;
  disabled?: boolean;
}

/**
 * The reason picker shown when an intake is marked non-admit (EMR-1595).
 *
 * Presentational only: the caller owns the state and decides when the choice
 * is valid. Recording a reason is optional, so the list leads with a skip
 * item; free text is required only for "Other".
 */
export const NonAdmitReasonFields: React.FC<NonAdmitReasonFieldsProps> = ({
  reason,
  details,
  onReasonChange,
  onDetailsChange,
  idPrefix = 'non-admit',
  disabled = false,
}) => {
  const detailsRequired = reason === NON_ADMIT_REASON_REQUIRING_DETAILS;
  const reasonId = `${idPrefix}-reason`;
  const detailsId = `${idPrefix}-reason-details`;

  return (
    <div className='space-y-4'>
      <div className='space-y-1.5'>
        <Label htmlFor={reasonId}>Reason (optional)</Label>
        <Select
          value={reason ?? SKIP_VALUE}
          disabled={disabled}
          onValueChange={value => {
            const next =
              value === SKIP_VALUE ? null : (value as NonAdmitReason);
            // The Textarea unmounts when the reason stops being the one that
            // needs it. Drop what was typed with it, or abandoned text rides
            // the next reason into the record, the export and the chip.
            if (next !== NON_ADMIT_REASON_REQUIRING_DETAILS && details) {
              onDetailsChange('');
            }
            onReasonChange(next);
          }}
        >
          <SelectTrigger
            id={reasonId}
            selectSize='sm'
            className='w-full md:w-72'
          >
            <SelectValue placeholder='No reason' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SKIP_VALUE}>No reason</SelectItem>
            {NON_ADMIT_REASON_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {detailsRequired && (
        <div className='space-y-1.5'>
          <Label htmlFor={detailsId}>Details</Label>
          <Textarea
            id={detailsId}
            value={details}
            disabled={disabled}
            textareaSize='sm'
            maxLength={NON_ADMIT_REASON_DETAILS_MAX_LENGTH}
            placeholder='Say why this intake was not admitted'
            onChange={event => onDetailsChange(event.target.value)}
          />
        </div>
      )}
    </div>
  );
};

export default NonAdmitReasonFields;
